import axios, { AxiosError } from 'axios'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { get, set } from 'lodash'
import useEffectiveAuthToken from '@/composables/useEffectiveAuthToken'

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    skipRedirectOn403?: boolean;
  }
}

export interface ErrorResponse extends AxiosError {
  errorMsg: string,
  errors: Array<string>
}

interface ErrorData {
  error?: string,
  detail?: string,
  reason?: string
}

export default defineNuxtPlugin(() => {
  const { getEffectiveAuthToken } = useEffectiveAuthToken()
  const { supportEmail } = useContactEmails()
  const defaultUrl = (<string>import.meta.env.VITE_API_ENDPOINT)

  const http = axios.create({
    baseURL: defaultUrl,
    headers: {
      common: {
        'Content-Language': 'en',
        'X-Ignore-Session': true
      }
    }
  })

  const requestInterceptor = {
    onFulfilled: async (config: InternalAxiosRequestConfig) => {
      const authToken = await getEffectiveAuthToken()
      if (authToken) {
        config.headers.Authorization = authToken
      }
      return config
    },
    onRejected: (error: AxiosError) => {
      throw error
    }
  }

  const responseInterceptor = {
    onFulfilled: (response: AxiosResponse) => response,
    onRejected: (error: ErrorResponse) => {
      if (error.code === 'ERR_NETWORK') {
        const message = 'We are having trouble reaching Emissary. Please check your internet connection and try again.'
        error.errorMsg = message
        error.errors = [message]
        throw error
      }
      const route = useRoute()
      const { response } = error
      const data = (response as AxiosResponse<ErrorData>)?.data
      const code = get(response, 'status', 0)
      const hasToSkipRedirection = route.name === 'login' || response?.config?.skipRedirectOn403
      const hasToRedirect = code === 401 || data?.detail === 'Error decoding token.'
      const hasToRedirectToStatus = data?.reason === 'suspended' && route.name !== 'logout'
      if (hasToRedirect && !hasToSkipRedirection) {
        if (hasToRedirectToStatus) {
          navigateTo('/account-status')
        } else {
          navigateTo({ path: '/logout', query: route?.query })
        }
      } else if (code === 403 && !hasToSkipRedirection) {
        navigateTo('/invalid-url')
      } else if (code === 400) {
        const errors = data ? Object.values(data).flat().map(String) : []
        error.errorMsg = errors.join(', ')
        error.errors = errors
      } else if (code === 429) {
        const message = `Too many attempts. Please wait a few minutes and try again, or contact ${supportEmail} to get unblocked.`
        error.errorMsg = message
        error.errors = [message]
      } else if (code >= 500) {
        const message = data?.error || 'Emissary is temporarily unavailable. Please try again in a few minutes.'
        error.errorMsg = message
        error.errors = [message]
      } else {
        error.errors = []
        const errorMessage = data?.error
        if (errorMessage) {
          error.errors.push(errorMessage)
        }
      }
      throw error
    }
  }

  http.interceptors.request.use(
    requestInterceptor.onFulfilled,
    requestInterceptor.onRejected
  )

  http.interceptors.response.use(
    responseInterceptor.onFulfilled,
    responseInterceptor.onRejected
  )

  set(http, '$get', (url: string, ...params: any[]) => {
    return http.get(url, ...params).then(response => response.data)
  })

  set(http, '$post', (url: string, ...params: any[]) => {
    return http.post(url, ...params).then(response => response.data)
  })

  set(http, '$patch', (url: string, ...params: any[]) => {
    return http.patch(url, ...params).then(response => response.data)
  })

  set(http, '$delete', (url: string, ...params: any[]) => {
    return http.delete(url, ...params).then(response => response.data)
  })

  return {
    provide: {
      axios: http
    }
  }
})
